<template>
  <div class="group relative bg-white dark:bg-gray-950 rounded-xl overflow-hidden vehicle-card-animation flex flex-col sm:flex-row lg:grid lg:grid-cols-[1fr_550px] p-0 sm:p-3 lg:p-4 ring ring-gray-950/5 dark:ring-white/10">
    <!-- Mobile Image (Visible at top on mobile only) -->
    <div class="relative sm:hidden">
      <!-- GROUP: Main mobile image + its interactive overlays -->
      <div class="relative">
        <div class="w-full h-80 overflow-hidden">
          <img
            v-if="currentMainImageUrl"
            :src="currentMainImageUrl"
            :alt="vehicle.title"
            class="h-full w-full object-cover"
            loading="lazy"
            @click="openQuickView(vehicle.id)"
          />
          <div v-else class="h-full w-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
            <span class="text-gray-400 text-sm font-medium px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-600">No Image</span>
          </div>
        </div>
        <div class="absolute inset-x-0 bottom-0 h-1/3 flex flex-col justify-end gap-2 bg-gradient-to-t from-black via-black/80 to-transparent p-4">
          <div class="flex items-center gap-2 text-white/90">
            <span class="pill-condition bg-green-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">{{ vehicle.condition.displayValue?.[0] || '' }}</span>
          </div>
          <span class="text-sm/6 font-semibold text-white/90">{{ determineVehicleType(vehicle) }}</span>
          <span class="text-lg/6 font-semibold text-white">{{ vehicle.title }}</span>
        </div>
        <!-- Mobile photo count indicator -->
        <div class="absolute bottom-2 right-2 z-10">
          <button
            class="bg-white/80 dark:bg-black/80 text-gray-800 dark:text-white text-xs px-2 py-1 rounded-md backdrop-blur-sm shadow-sm"
            @click="openQuickView(vehicle.id)"
          >
            {{ getPhotoCount }} photos
          </button>
        </div>
        <!-- Favorite and Cart Icons (mobile) -->
        <div class="absolute top-3 right-3 z-10 flex flex-col space-y-1 bg-white/70 dark:bg-black/70 backdrop-blur-sm p-1.5 rounded-lg shadow-lg">
          <FavoriteButton v-if="vehicle.id" :vehicle-id="vehicle.id" />
          <button
            v-if="vehicle.id"
            @click="addToCart(vehicle.id)"
            aria-label="Add to Garage"
            class="text-gray-900 dark:text-white hover:text-pink-600 dark:hover:text-pink-500 focus:outline-none transition-colors p-1 flex items-center justify-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-shopping-cart"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>
          </button>
        </div>
      </div>

      <!-- Mobile Landscape Secondary Images -->
      <div class="hidden mobile-landscape-secondary-images-container grid-cols-2 gap-1 mt-1 mx-1 mb-1">
          <template v-for="(imgUrl) in displayedSecondaryImages.slice(0, 2)" :key="'mobile-sec-' + (imgUrl || 'no-img')">
              <div
                  class="relative w-full h-24 rounded overflow-hidden cursor-pointer shadow"
                  @click="imgUrl && setMainImage(imgUrl)"
              >
                  <img
                      v-if="imgUrl"
                      :src="imgUrl"
                      :alt="`${vehicle.title} - photo ${props.vehicle.photos && props.vehicle.photos.indexOf(imgUrl) !== -1 ? props.vehicle.photos.indexOf(imgUrl) + 1 : 'secondary'}`"
                      class="h-full w-full object-cover"
                      loading="lazy"
                  />
                  <div v-else class="h-full w-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center rounded">
                      <span class="text-gray-400 text-xs">No Image</span>
                  </div>
              </div>
          </template>
      </div>
    </div>

    <!-- Left Content (Title, Details, Description) -->
    <div class="flex-1 pr-0 sm:pr-6 lg:pr-8 p-4 sm:p-4 mt-0 sm:mb-0 flex flex-col h-full">
      <div class="flex flex-col h-full flex-grow">
        <!-- Title and Type (Hidden on mobile, visible on sm+) -->
        <div class="hidden sm:block mb-1 flex-shrink-0">
          <div class="text-xs sm:text-sm font-medium text-gray-500 dark:text-gray-500">
            {{ determineVehicleType(vehicle) }}
          </div>
          <h2 class="text-lg sm:text-xl md:text-3xl font-semibold text-gray-950 dark:text-white">
            {{ vehicle.title }}
          </h2>
        </div>

        <!-- Condition & Location (Hidden on mobile, visible on sm+) -->
        <div class="hidden sm:flex mt-2 flex-wrap gap-2 items-center flex-shrink-0">
          <span class="pill-condition bg-green-500 text-white px-2 py-0.5 rounded-full text-xs font-medium">{{ vehicle.condition.displayValue?.[0] || '' }}</span>
          <span class="text-pink-300 dark:text-gray-600">·</span>
          <span class="pill-location bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 px-2 py-0.5 rounded-full text-xs font-medium">
            {{ formatLocation(vehicle) }}
          </span>
        </div>

        <!-- Vehicle Attributes (now placed here) -->
        <div class="flex flex-wrap gap-2 mb-2 mt-3 flex-shrink-0">
          <!-- Transmission -->
          <span v-if="vehicle.transmission?.displayValue?.[0]" class="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-full text-sm font-semibold">
            {{ vehicle.transmission?.displayValue?.[0] }}
          </span>

          <!-- Fuel Type -->
          <span v-if="vehicle.fuel?.displayValue?.[0]" class="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-full text-sm font-semibold">
            {{ vehicle.fuel?.displayValue?.[0] }}
          </span>

          <!-- Odometer -->
          <span v-if="vehicle.kms || vehicle.mileage" class="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-full text-sm font-semibold">
            {{ formatKilometers(vehicle.kms || vehicle.mileage) }}
          </span>

          <!-- Body Type -->
          <span v-if="vehicle.model?.displayBody?.[0] || vehicle.body?.displayValue?.[0]" class="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-full text-sm font-semibold">
            {{ vehicle.model?.displayBody?.[0] || vehicle.body?.displayValue?.[0] }}
          </span>

          <!-- Drivetrain -->
          <span v-if="vehicle.drivetrain?.displayValue?.[0]" class="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-full text-sm font-semibold">
            {{ vehicle.drivetrain?.displayValue?.[0] }}
          </span>

          <!-- Engine -->
          <span v-if="vehicle.engine?.displayValue?.[0] || formatEngine(vehicle) !== 'Not specified'" class="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-full text-sm font-semibold">
            {{ vehicle.engine?.displayValue?.[0] || formatEngine(vehicle) }}
          </span>

          <!-- Seats -->
          <span v-if="formattedSeats !== 'Not specified'" class="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-full text-sm font-semibold">
            {{ formattedSeats }}
          </span>

          <!-- Doors -->
          <span v-if="formattedDoors !== 'Not specified'" class="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-full text-sm font-semibold">
            {{ formattedDoors }}
          </span>

          <!-- Cylinders -->
          <span v-if="formattedCylinders !== 'Not specified'" class="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-full text-sm font-semibold">
            {{ formattedCylinders }}
          </span>

          <!-- Engine Size -->
          <span v-if="formattedEngineSizeL !== 'Not specified'" class="pill bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 px-4 py-2 rounded-full text-sm font-semibold">
            {{ formattedEngineSizeL }}
          </span>
        </div>

        <!-- Description -->
        <div class="mt-4 mb-4 flex-grow min-h-[90px] flex flex-col justify-between">
          <div class="text-md text-gray-600 dark:text-gray-400 flex-grow" 
               :class="{'overflow-y-auto': showFullDescription}">
            <span :class="{'line-clamp-3': !showFullDescription}">
              {{ vehicle.description || generateDescription(vehicle) }}
            </span>
            <button v-if="(vehicle.description || generateDescription(vehicle)).length > 100"
              class="ml-1 inline-flex flex-shrink-0 text-sm/6 font-semibold text-pink-600 dark:text-pink-500"
              @click="toggleShowMore">
              {{ showFullDescription ? '... show less' : '... show more' }}
            </button>
          </div>
        </div>

        <!-- Price and Action Buttons Row -->
        <div class="mt-auto flex-shrink-0">
          <div class="flex flex-col [1660px]:flex-row [1660px]:items-center [1660px]:justify-between gap-y-3">
            <!-- Pricing Section (Left) -->
            <div class="grid grid-cols-2 gap-3 max-w-full">
              <!-- Drive Away Price -->
              <div v-if="vehicle.price" class="h-[64px] px-5 py-2 rounded-md text-left flex flex-col justify-center">
                <p class="text-xl sm:text-3xl font-bold text-gray-900 dark:text-white leading-tight">
                  ${{ vehicle.price.toLocaleString() }}*
                </p>
                <p class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-0.5">DRIVE AWAY</p>
              </div>

              <!-- Weekly Repayments -->
              <div v-if="vehicle.perweek" class="h-[64px] px-5 py-2 rounded-md text-left flex flex-col justify-center">
                <p class="text-xl sm:text-2xl font-bold leading-tight text-gray-900 dark:text-white">
                  From ${{ formatPerWeek(vehicle.perweek) }}<span class="text-sm font-normal text-gray-600 dark:text-gray-300">/week</span>
                </p>
                <p class="text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider mt-0.5">*Finance est. 9.8%/5yrs.</p>
              </div>
              
              <!-- Contact for Price -->
              <div v-if="!vehicle.price && !vehicle.perweek" class="h-[64px] bg-gray-100 dark:bg-gray-800/70 px-5 py-2 rounded-md text-center flex flex-col justify-center col-span-2">
                <p class="text-lg sm:text-xl font-medium text-gray-900 dark:text-white">
                  Contact us for price
                </p>
              </div>
            </div>
            
            <!-- Action Buttons (Right) -->
            <div class="grid grid-cols-3 gap-2 mt-3 [1660px]:mt-0">
              <button
                @click="openQuickView(vehicle.id)"
                class="h-[56px] whitespace-nowrap rounded-md bg-transparent border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
              >
                Enquire Now
              </button>
              <NuxtLink
                class="h-[56px] flex items-center justify-center text-center whitespace-nowrap rounded-md bg-black border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium text-white hover:bg-gray-900 transition-colors"
                :to="`/vehicle-for-sale/${vehicle.stockid || vehicle.id}/${vehicle.slug}`"
              >
                View Details
              </NuxtLink>
              <button
                @click="addToCart(vehicle.id)"
                class="h-[56px] whitespace-nowrap rounded-md bg-transparent border border-gray-300 dark:border-gray-700 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-900 transition-colors"
              >
                Add to Garage
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Content (Images - hidden on mobile) -->
    <div class="w-full sm:w-[45%] lg:w-[550px] relative rounded-lg overflow-hidden hidden sm:block">
      
      <!-- Tablet Image Layout (sm to lg) -->
      <div class="hidden sm:block lg:hidden h-full">
        <!-- Single large image for tablet -->
        <div class="relative rounded-lg overflow-hidden h-full min-h-[60vh] sm:min-h-[40vh] md:min-h-[300px]">
          <img
            v-if="mainImage"
            :src="mainImage"
            :alt="vehicle.title"
            class="h-full w-full object-cover"
            loading="lazy"
          />
          <div v-else class="h-full w-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
            <span class="text-gray-400 text-sm font-medium px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-600">No Image</span>
          </div>
          
          <!-- Photo count indicator (tablet) -->
          <div class="absolute bottom-2 right-2 z-10">
            <button 
              class="bg-white/80 dark:bg-black/80 text-gray-800 dark:text-white text-xs px-2 py-1 rounded-md backdrop-blur-sm shadow-sm"
              @click="openQuickView(vehicle.id)"
            >
              {{ getPhotoCount }} photos
            </button>
          </div>
        </div>
      </div>
      
      <!-- Desktop Image Layout (lg and up) -->
      <div class="hidden lg:flex flex-col h-full">
        <!-- Main (hero) image at the top -->
        <div class="relative rounded-lg overflow-hidden flex-grow min-h-[180px] xl:min-h-[240px] w-full mb-2">
          <img
            v-if="currentMainImageUrl"
            :src="currentMainImageUrl"
            :alt="vehicle.title"
            class="h-full w-full object-cover rounded-lg"
            loading="lazy"
            @click="openQuickView(vehicle.id)" 
          />
          <div v-else class="h-full w-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center rounded-lg">
            <span class="text-gray-400 text-sm font-medium px-3 py-1 rounded-full bg-gray-100 dark:bg-gray-600">No Image</span>
          </div>
          <!-- View all photos button, if more than 1 photo -->
          <div v-if="getPhotoCount > 1" class="absolute bottom-2 right-2 z-10">
            <button 
              class="bg-white/80 dark:bg-black/80 text-gray-800 dark:text-white text-xs px-3 py-1.5 rounded-full backdrop-blur-sm shadow-sm hover:bg-white dark:hover:bg-black transition-colors"
              @click="openQuickView(vehicle.id)"
            >
              View all {{ getPhotoCount }} photos
            </button>
          </div>
        </div>
        <!-- Secondary images below the main image -->
        <div v-if="displayedSecondaryImages.length > 0" class="grid grid-cols-4 gap-2 mt-1 h-[50px] lg:h-[90px]">
          <template v-for="(imgUrl, idx) in displayedSecondaryImages" :key="imgUrl || idx">
            <div 
              class="relative w-full h-full rounded-lg overflow-hidden cursor-pointer"
              @click="setMainImage(imgUrl)"
            >
              <img
                v-if="imgUrl"
                :src="imgUrl"
                :alt="`${vehicle.title} - photo ${props.vehicle.photos.indexOf(imgUrl) + 1}`"
                class="h-full w-full object-cover rounded-lg"
                loading="lazy"
              />
              <div v-else class="h-full w-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center rounded-lg">
                <span class="text-gray-400 text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-600">No Image</span>
              </div>
            </div>
          </template>
        </div>
      </div>

      <!-- Favorite and Cart Icons (tablets and desktop) -->
      <div class="absolute top-3 right-3 z-10 hidden sm:flex flex-col space-y-1 bg-white/70 dark:bg-black/70 backdrop-blur-sm p-1.5 rounded-lg shadow-lg">
        <FavoriteButton v-if="vehicle.id" :vehicle-id="vehicle.id" />
        <button
          v-if="vehicle.id"
          @click="addToCart(vehicle.id)"
          aria-label="Add to Garage"
          class="text-gray-900 dark:text-white hover:text-pink-600 dark:hover:text-pink-500 focus:outline-none transition-colors p-1 flex items-center justify-center"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-shopping-cart"><circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/></svg>
        </button>
      </div>
      
      <!-- Sidebar divider on larger screens -->
      <div class="absolute top-[186px] -right-4 z-50 -mt-6 h-12 w-1.5 rounded-full max-lg:hidden bg-slate-950/20 dark:bg-slate-500"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import FavoriteButton from '~/layers/base/components/FavoriteButton.vue';
import { useVehicleQuickView } from '~/layers/base/composables/useVehicleQuickView';
import { useCartStore } from '~/stores/cartStore';

const { openQuickView } = useVehicleQuickView();
const cartStore = useCartStore();
const showFullDescription = ref(false);

const props = defineProps({
  vehicle: {
    type: Object,
    required: true,
    default: () => ({
      id: '',
      stockid: '',
      slug: '',
      title: '',
      description: '',
      image: '',
      thumb: '',
      photos: [],
      year: null,
      mileage: null,
      kms: null,
      price: null,
      perweek: null,
      make: {},
      model: {},
      badge: null,
      condition: { displayValue: [''] }, // Default condition structure
      body: {},
      transmission: null,
      fuel: null,
      drivetrain: null,
      engine: null,
      address: null,
      seats: null, // Added for new pill
      doors: null, // Added for new pill
      attributes: [] // Added for new pills
    })
  }
});

// State for the currently displayed hero image
const currentMainImageUrl = ref<string | undefined>();

// Function to change the main hero image
function setMainImage(imageUrl: string) {
  currentMainImageUrl.value = imageUrl;
}

// Initialize and update currentMainImageUrl when vehicle prop changes
watch(
  () => [props.vehicle.photos, props.vehicle.thumb, props.vehicle.image],
  ([newPhotosUntyped, newThumb, newImage]) => {
    const newPhotos = (newPhotosUntyped || []) as string[]; // Ensure it's an array

    const defaultHeroFromPhotos = (newPhotos.length > 0 && newPhotos[0]) ? newPhotos[0] : undefined;
    const finalDefaultHero = defaultHeroFromPhotos || newThumb || newImage || undefined;
    
    let resetToDefault = true;
    if (currentMainImageUrl.value && newPhotos.length > 0) {
      if (newPhotos.includes(currentMainImageUrl.value)) {
        resetToDefault = false; // User's selection is still valid, don't reset
      }
    }

    if (resetToDefault) {
      currentMainImageUrl.value = finalDefaultHero;
    }
    // If !resetToDefault, currentMainImageUrl.value is preserved.
  },
  { immediate: true, deep: true }
);

// Add to cart function
const addToCart = (vehicleId: string) => {
  if (vehicleId) {
    cartStore.addToCart(vehicleId);
  }
};

// Get the main image for the larger slot
const mainImage = computed(() => {
  // This computed property still defines the "default" main image,
  // primarily used by mobile/tablet views or as an initial source for currentMainImageUrl.
  if (props.vehicle.photos && props.vehicle.photos.length > 0 && props.vehicle.photos[0]) {
    return props.vehicle.photos[0];
  }
  // Fall back to thumb or image properties
  return props.vehicle.thumb || props.vehicle.image;
});

// Get secondary images for the smaller slots (filter out nulls and the current main image)
const displayedSecondaryImages = computed(() => {
  if (props.vehicle.photos && props.vehicle.photos.length > 0) {
    return props.vehicle.photos
      .filter((imgUrl: string | null | undefined) => imgUrl && imgUrl !== currentMainImageUrl.value)
      .slice(0, 4);
  }
  return [];
});

// Get total photo count based on the photos array
const getPhotoCount = computed(() => {
  return (props.vehicle.photos || []).filter(Boolean).length;
});

// Function to format the location
const formatLocation = (vehicle: any): string => {
  if (vehicle.address) {
    const parts = [];
    if (vehicle.address.suburb) parts.push(vehicle.address.suburb);
    if (vehicle.address.state) parts.push(vehicle.address.state);
    if (parts.length > 0) return parts.join(', ');
  }
  
  // Handle legacy location field if present
  if (vehicle.location) {
    return vehicle.location;
  }
  
  return 'Location not specified';
};

// Function to format the year
const formatYear = (vehicle: any): string => {
  if (vehicle.year) {
    if (typeof vehicle.year === 'object' && vehicle.year.displayValue && vehicle.year.displayValue.length) {
      return vehicle.year.displayValue[0];
    }
    return String(vehicle.year);
  }
  return 'Year not specified';
};

// Function to format the perweek value
const formatPerWeek = (perweek: any): string => {
  let value: number;
  if (typeof perweek === 'number') {
    value = perweek;
  } else if (perweek && typeof perweek === 'object' && 'value' in perweek) {
    value = Array.isArray(perweek.value) && perweek.value.length > 0
      ? Number(perweek.value[0])
      : 0;
  } else {
    value = 0;
  }
  return Math.round(value).toString();
};

// Toggle description visibility
const toggleShowMore = () => {
  showFullDescription.value = !showFullDescription.value;
};

// Format kilometers 
function formatKilometers(kms: number | null | undefined): string {
  if (!kms) return 'Not specified';
  return `${kms.toLocaleString()} km`;
}

// Format engine information
function formatEngine(vehicle: any): string {
  if (vehicle.engine?.displayValue?.[0]) {
    return vehicle.engine.displayValue[0];
  }

  // Check for engine size in attributes
  if (vehicle.attributes) {
    const engineAttr = vehicle.attributes.find((attr: any) => 
      attr.Name === 'EngineSize' || attr.Name === 'Engine');
    if (engineAttr) return engineAttr.Value;
  }

  // Fallback to constructing from fuel type if engine info not available
  if (vehicle.fuel?.displayValue?.[0]) {
    const fuelType = vehicle.fuel.displayValue[0];
    if (fuelType.toLowerCase().includes('diesel')) {
      return 'Diesel';
    }
    if (fuelType.toLowerCase().includes('petrol')) {
      return 'Petrol';
    }
    return fuelType;
  }

  return 'Not specified';
}

// Determine vehicle type based on body type or category
const determineVehicleType = (vehicle: any): string => {
  if (vehicle.make?.displayValue?.[0]) {
    const make = vehicle.make.displayValue[0];
    
    // If we have both make and model, return them together
    if (vehicle.model?.displayValue?.[0]) {
      return `${make} ${vehicle.model.displayValue[0]}`;
    }
    
    return make;
  }
  
  if (vehicle.model?.displayBody?.[0]) {
    return vehicle.model.displayBody[0];
  }
  
  if (vehicle.body?.displayValue?.[0]) {
    return vehicle.body.displayValue[0];
  }
  
  // Extract vehicle type from title
  const titleParts = vehicle.title?.split(' ') || [];
  if (titleParts.length >= 3) {
    return titleParts.slice(0, 2).join(' '); // Often the first 2 words are the make+model
  }
  
  return 'Vehicle';
};

// Determine vehicle condition based on mileage and year
const determineCondition = (vehicle: any): string => {
  const mileage = vehicle.kms || vehicle.mileage;
  const currentYear = new Date().getFullYear();
  const vehicleYear = vehicle.year;
  
  // If condition is explicitly set, use that
  if (vehicle.condition) {
    return vehicle.condition;
  }
  
  // If the vehicle is less than 3 years old and has low mileage
  if (vehicleYear && currentYear - vehicleYear < 3 && mileage && mileage < 40000) {
    return 'Excellent';
  }
  
  // If the vehicle is less than 5 years old or has moderate mileage
  if ((vehicleYear && currentYear - vehicleYear < 5) || (mileage && mileage < 80000)) {
    return 'Very Good';
  }
  
  // If the vehicle is less than 8 years old or has higher mileage
  if ((vehicleYear && currentYear - vehicleYear < 8) || (mileage && mileage < 120000)) {
    return 'Good';
  }
  
  // Default condition for older or high-mileage vehicles
  return 'Fair';
};

// Generate a placeholder description if none is provided
const generateDescription = (vehicle: any): string => {
  const year = formatYear(vehicle);
  let make = 'premium';
  let model = 'vehicle';
  
  if (vehicle.make?.displayValue?.[0]) {
    make = vehicle.make.displayValue[0];
  }
  
  if (vehicle.model?.displayValue?.[0]) {
    model = vehicle.model.displayValue[0];
  } else if (vehicle.title) {
    // Try to extract from title
    const titleParts = vehicle.title.split(' ');
    if (titleParts.length >= 2) {
      make = titleParts[1];
      if (titleParts.length >= 3) {
        model = titleParts[2];
      }
    }
  }
  
  const kms = vehicle.kms || vehicle.mileage || '40,000';
  const transmission = vehicle.transmission?.displayValue?.[0] || 'automatic';
  const fuel = vehicle.fuel?.displayValue?.[0] || 'efficient';
  const body = vehicle.body?.displayValue?.[0] || vehicle.model?.displayBody?.[0] || 'stylish';
  const drivetrain = vehicle.drivetrain?.displayValue?.[0] || 'responsive';
  const engine = vehicle.engine?.displayValue?.[0] || formatEngine(vehicle) || 'powerful';
  const condition = vehicle.condition?.displayValue?.[0] || determineCondition(vehicle);
  
  return `This ${year} ${make} ${model} is a stunning example of modern automotive engineering. With just ${kms} kilometers on the odometer, this ${transmission} ${fuel} vehicle is in ${condition.toLowerCase()} condition and ready for its new owner. 

The ${body} body style offers excellent visibility and comfort, while the ${drivetrain} drivetrain ensures confident handling in all conditions. Powered by a ${engine} engine, this ${make} delivers responsive acceleration and impressive fuel efficiency for its class.

Features include modern comforts like climate control and premium audio, advanced safety systems including lane departure warning and autonomous emergency braking, and impressive performance capabilities that make every drive enjoyable. Whether commuting daily or planning weekend getaways, this ${model} offers the perfect balance of practicality, comfort, and driving pleasure.`;
};

// Computed property for formatted seats
const formattedSeats = computed(() => {
  const seats = props.vehicle.seats?.displayValue?.[0] || props.vehicle.attributes?.find((attr: { Name: string; Value: string }) => attr.Name === 'Seats')?.Value;
  return seats ? `${seats} Seats` : 'Not specified';
});

// Computed property for formatted doors
const formattedDoors = computed(() => {
  const doors = props.vehicle.doors?.displayValue?.[0] || props.vehicle.attributes?.find((attr: { Name: string; Value: string }) => attr.Name === 'Doors')?.Value;
  return doors ? `${doors} Doors` : 'Not specified';
});

// Computed property for formatted cylinders
const formattedCylinders = computed(() => {
  const cylindersAttr = props.vehicle.attributes?.find((attr: { Name: string; Value: string }) => attr.Name === 'Cylinders');
  return cylindersAttr?.Value ? `${cylindersAttr.Value} Cyl` : 'Not specified';
});

// Computed property for formatted engine size in Liters
const formattedEngineSizeL = computed(() => {
  const engineSizeAttr = props.vehicle.attributes?.find((attr: { Name: string; Value: string }) => attr.Name === 'EngineSize');
  if (engineSizeAttr?.Value) {
    const sizeInCC = parseInt(engineSizeAttr.Value, 10);
    if (!isNaN(sizeInCC) && sizeInCC > 0) {
      return `${(sizeInCC / 1000).toFixed(1)}L`;
    }
  }
  return 'Not specified';
});
</script>

<style scoped>
/* Custom breakpoint for extra small devices */
@custom-media --xs (min-width: 480px);

/* Responsive Breakpoints Documentation:
 * xs: 480px+ (Extra small devices - large phones)
 * sm: 640px+ (Small devices - tablets, small laptops)
 * md: 768px+ (Medium devices - tablets landscape, laptops)
 * lg: 1024px+ (Large devices - desktops, large laptops)
 * xl: 1280px+ (Extra large devices - large desktops)
 *
 * Mobile: Image first with overlay text, followed by details and CTA
 * Small Tablet (sm): 2-column layout with text on left and photo grid on right  
 * Medium Desktop (md): Grid layout with main content and 450px sidebar
 * XL: Simplified design with large main image and two secondary images
 */

.vehicle-card-animation {
  opacity: 0;
  transform: translateY(20px);
  animation: fadeInUp 0.5s ease-out forwards;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.vehicle-card-animation:hover {
  transform: translateY(-5px);
}

.pill {
  display: inline-flex;
  align-items: center;
  transition: all 0.2s ease;
}

.pill:hover {
  background-color: rgba(236, 72, 153, 0.1);
  color: rgba(236, 72, 153, 0.8);
}

.pill-rating {
  display: inline-flex;
  align-items: center;
}

.pill-condition {
  display: inline-flex;
  align-items: center;
}

.pill-location {
  display: inline-flex;
  align-items: center;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive utilities */
@media (min-width: 480px) {
  .xs\:flex-row {
    flex-direction: row;
  }
  .xs\:grid-cols-2 {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

/* Mobile landscape secondary images */
@media (min-width: 480px) and (max-width: 639.98px) and (orientation: landscape) {
  .mobile-landscape-secondary-images-container {
    display: grid;
  }
}
</style>